<template>
  <div
    class="region-countries-list bg-white border"
    data-test="region-countries-list"
  >
    <b-button
      variant="btn-secondary"
      class="form-control-plaintext form-control text-left"
      @click="openDropdown"
      data-test="geo-targeting-button"
      :disabled="readOnly"
    >
      Select targeting
    </b-button>
    <div
      class="region-countries-list__results"
      v-if="isOpen"
    >
      <fieldset class="form-fieldset mb-0">
        <div class="form-group mb-0">
          <label class="form-label">Country search</label>
          <div class="row gutters-sm">
            <div class="col">
              <input
                type="text"
                class="form-control"
                v-model="countrySearchValue"
                @keyup="countrySearch"
                ref="countrySearch"
              >
            </div>
            <span class="col-auto">
              <button
                class="btn btn-secondary"
                type="button"
                @click="resetSearch();$refs.countrySearch.focus();"
              >
                <i class="fa-light fa-times" />
              </button>

            </span>
          </div>
        </div>
      </fieldset>
      <ul
        ref="ul"
      >
        <div
          v-if="isLoading"
          class="h-100 align-items-center d-flex justify-content-center card-body"
        >
          <div class="loader mx-auto" />
        </div>
        <template
          v-else
          v-for="region in displayRegions"
        >
          <li
            :key="region.id"
            ref="li"
            class="bg-white"
            :data-test="`${region.name}-label`"
          >
            {{ region.name }} <small class="ml-5">({{ region.countries.length }})</small>
            <div class="selectgroup selectgroup-pills ml-auto">
              <b-button
                variant="link"
                @click="regionSelect(region.id)"
              >
                {{ isInSelectedRegion(region.id)?'deselect all':'select all' }}
              </b-button>
              <span
                class="selectgroup-button selectgroup-button-icon"
                v-b-toggle="`accordion-${region.id}`"
                :data-test="`${region.name}-collapse-button`"
              >
                <i
                  class="when-closed fa far fa-angle-down"
                />
                <i
                  class="when-opened fa far fa-angle-up"
                />
              </span>
            </div>
          </li>
          <b-collapse
            :id="`accordion-${region.id}`"
            :key="`region${region.id}`"
            accordion="my-accordion"
            role="tabpanel"
            visible
          >
            <li
              v-if="region.countries.length === 0"
              class="pl-5 bg-light"
            >
              No Matches
            </li>
            <li
              v-for="(countryId,index) in region.countries"
              :key="`countries${countryId}`"
              class="pl-5"
              :class="{'bg-light': index >= 0}"
              ref="li"
              :data-test="`${region.name}-${targetingCountries[countryId].name}-label`"
            >
              {{ targetingCountries[countryId].name }}
              <div
                class="selectgroup selectgroup-pills ml-auto"
                v-if="!!targetingCountries[countryId]"
              >
                <BaseRadio
                  name="options"
                  :key="`include${region.id}${countryId}`"
                  label="include"
                  :val="targetingCountries[countryId].selectedValue"
                  icon="fa-check"
                  @change="changeValue($event,countryId, region.id)"
                  :data-test="`${region.name}-${targetingCountries[countryId].name}-include-button`"
                />
                <BaseRadio
                  name="options"
                  :key="`exclude${region.id}${countryId}`"
                  label="exclude"
                  :val="targetingCountries[countryId].selectedValue"
                  icon="fa-times"
                  @change="changeValue($event,countryId, region.id)"
                  :data-test="`${region.name}-${targetingCountries[countryId].name}-exclude-button`"
                />
              </div>
            </li>
          </b-collapse>
        </template>
      </ul>
    </div>
  </div>
</template>

<script>
import helper from '@/core/helper';

import { mapActions } from 'vuex';
import * as types from '@/modules/campaigns/store/types';

import BaseRadio from '@/components/form/BaseRadio';

export default {
  name: 'RegionCountriesList',
  components: {
    BaseRadio,
  },
  data() {
    return {
      isLoading: true,
      isOpen: false,
      targetingCountries: {},
      countries: {},
      regions: [],
      displayRegions: [],
      countrySearchValue: '',
    };
  },
  props: {
    targeting: {
      type: Object,
      required: true,
      default: () => ({
        regions: [],
        countryInclusionsExclusions: {
          inclusions: [],
          exclusions: [],
        },
      }),
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.FETCH_COUNTRIES().then((response) => {
      this.regions = response.regions;
      this.countries = response.countries;
      this.setTargeting();
      this.sortRegionsCommonCounties();
      this.isLoading = !this.targetingCountriesLoaded;
    });
    document.addEventListener('click', this.onClickOutside);
  },
  destroyed() {
    document.removeEventListener('click', this.onClickOutside);
  },
  computed: {
    targetingCountriesLoaded() {
      return Object.keys(this.targetingCountries).length > 0;
    },
  },
  methods: {
    ...mapActions('campaigns', [
      types.FETCH_COUNTRIES,
      types.FETCH_COMMON_COUNTIES,
    ]),
    getValue(id) {
      return this.targetingCountries[id].selectedValue;
    },
    changeValue(label, id, regionId) {
      const { selectedValue } = this.targetingCountries[id];
      if (label !== selectedValue) {
        this.$set(this.targetingCountries[id], 'selectedValue', label);
        this.$emit('addCountry', label, id, regionId);
      }
    },
    openDropdown() {
      this.resetSearch();
      this.isOpen = !this.isOpen;
      this.$nextTick(() => {
        if (this.$refs.countrySearch) this.$refs.countrySearch.focus();
      });
    },
    onClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
        this.resetSearch();
      }
    },
    isInSelectedRegion(key) {
      return !!this.targeting.regions.some((region) => Number(region) === Number(key));
    },
    isCountryInSelectedRegion(key) {
      return this.targeting.regions.some((region) => {
        const { countries } = this.regions.find((item) => Number(item.id) === Number(region));
        return !!countries.find((country) => Number(country) === Number(key));
      });
    },
    setTargeting() {
      Object.keys(this.countries).forEach((key) => {
        let selectedValue;
        if (this.targeting.countryInclusionsExclusions.inclusions.find((country) => Number(country) === Number(key))) selectedValue = 'include';
        else if (this.isCountryInSelectedRegion(key)) selectedValue = 'include';
        else selectedValue = '';
        if (this.targeting.countryInclusionsExclusions.exclusions.find((country) => Number(country) === Number(key))) selectedValue = 'exclude';
        this.$set(this.targetingCountries, Number(key), {
          name: this.countries[key],
          selectedValue,
        });
      });
    },
    regionSelect(key) {
      if (this.isInSelectedRegion(key)) this.$emit('removeRegion', key);
      else this.$emit('addRegion', key);
    },
    async sortRegionsCommonCounties() {
      let commonCounties;
      await this.FETCH_COMMON_COUNTIES()
        .then((counties) => {
          commonCounties = counties;
        });
      const sortOrder = commonCounties;
      const sortedRegions = this.regions
        .map((region) => {
          const items = region.countries;

          const sortByCommon = items.sort((a, b) => sortOrder
            .indexOf(this.targetingCountries[a].name) - sortOrder
            .indexOf(this.targetingCountries[b].name));

          const topItems = sortByCommon.slice(0, 3);

          const sortCountries = items.sort((a, b) => {
            if (this.targetingCountries[a].name < this.targetingCountries[b].name) return -1;
            if (this.targetingCountries[a].name > this.targetingCountries[b].name) return 1;
            return 0;
          });
          const countriesFiltered = sortCountries.filter((val) => topItems.indexOf(val) === -1);
          return {
            ...region,
            countries: [...topItems, ...countriesFiltered],
          };
        });
      this.regions = sortedRegions;
      this.displayRegions = sortedRegions;
    },
    countrySearch: helper.debounce(async function countrySearch() {
      const filtered = this.regions.map((r) => {
        const mappedRegions = r.countries
          .map((c) => ({ key: c, value: this.countries[c] }));
        const filteredRegions = mappedRegions
          .filter((c) => c.value.toLowerCase().startsWith(this.countrySearchValue.toLowerCase()));
        return {
          id: r.id,
          name: r.name,
          countries: filteredRegions
            .map((item) => item.key),
        };
      });
      this.displayRegions = filtered;
    }, 1000),
    resetSearch() {
      this.countrySearchValue = '';
      this.displayRegions = this.regions;
    },
  },
};
</script>
<style lang="scss">
.region-countries-list{
    position: relative;
  &__results {
    overflow: auto;
    max-height: 450px;
    position: absolute;
    bottom: 38px;
    z-index: 10;
    width: 100%;
  }
ul {
    padding: 0;
    margin: 0;
    position: relative;
  }

  li {
    list-style: none;
    text-align: left;
    padding: 4px 10px;
    line-height: 40px;
    display: flex;
  }
}
    .collapsed > .when-opened,
    :not(.collapsed) > .when-closed {
        display: none;
    }
</style>
